import '@assets/styles/index.css';
import 'dayjs/locale/ja';

import ErrorBoundary from '@src/components/ErrorBoundary.tsx';
import GoogleAnalytics from '@src/components/GoogleAnalytics.tsx';
import Error404 from '@src/pages/Error404.tsx';
import Error429 from '@src/pages/Error429.tsx';
import Error500 from '@src/pages/Error500.tsx';
import { store } from '@src/store/store.ts';
import { getPathname } from '@src/utils/urlUtils.ts';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import React, { useContext } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { StaticRouter } from 'react-router-dom/server';

import { SharedAuthProvider } from './context/auth/SharedAuthProvider';
import { TenantAuthProvider } from './context/auth/TenantAuthProvider';
import { ChatEditingProvider } from './context/ChatEditingContext';
import { InAppNotificationProvider } from './context/InAppNotificationContext';
import { IsModalProvider } from './context/IsModalContext';
import { NavigationContext, NavigationProvider } from './context/NavigationContext';
import { ReactionProvider } from './context/ReactionContext';
import { UnsavedInviteUsersProvider } from './context/UnsavedInviteUsersContext';
import ToastProvider from './features/toast';
import { CompanyProviders } from './providers/CompanyProviders';
import companyRoutes from './routes/company';
import sharedRoutes from './routes/shared';
import tenantRoutes from './routes/tenant';
import { responsiveStylesType } from './types/responsiveStylesType';

dayjs.extend(localizedFormat);
dayjs.locale('ja');

const renderResponsiveStyles = (responsiveList: responsiveStylesType) => {
  if (!responsiveList) {
    return '';
  }
  let styles = '';
  if (
    responsiveList.includes('desktop') &&
    responsiveList.includes('tablet') &&
    responsiveList.includes('mobile') &&
    responsiveList.length === 3
  ) {
    styles = '';
  }
  if (
    responsiveList.includes('desktop') &&
    responsiveList.includes('tablet') &&
    responsiveList.length === 2
  ) {
    styles = 'body { min-width: 768px; }';
  }
  if (responsiveList.includes('desktop') && responsiveList.length === 1) {
    styles = 'body { min-width: 960px; }';
  }
  return <style>{styles}</style>;
};

function Content() {
  const { errorStatus } = useContext(NavigationContext)!;
  const isTenant: boolean = getPathname().includes('/tenant');

  if (errorStatus === 404) {
    return <Error404 isTenant={isTenant} />;
  }
  if (errorStatus === 429) {
    return <Error429 />;
  }
  if (errorStatus === 500) {
    return <Error500 isTenant={isTenant} />;
  }

  return (
    <Routes>
      {tenantRoutes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <TenantAuthProvider>
              {renderResponsiveStyles(route.responsive)}
              {route.element}
            </TenantAuthProvider>
          }
        />
      ))}
      {companyRoutes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <CompanyProviders>
              {renderResponsiveStyles(route.responsive)}
              {route.element}
            </CompanyProviders>
          }
        />
      ))}
      {sharedRoutes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <UnsavedInviteUsersProvider>
              <SharedAuthProvider>
                <InAppNotificationProvider>
                  <TenantAuthProvider>
                    <CompanyProviders>
                      <IsModalProvider>
                        <ChatEditingProvider>
                          <ReactionProvider>
                            {renderResponsiveStyles(route.responsive)}
                            {route.element}
                          </ReactionProvider>
                        </ChatEditingProvider>
                      </IsModalProvider>
                    </CompanyProviders>
                  </TenantAuthProvider>
                </InAppNotificationProvider>
              </SharedAuthProvider>
            </UnsavedInviteUsersProvider>
          }
        />
      ))}
      <Route
        path='*' // TODO *とtenant/*でTenant404とCompany404を分割する
        element={<Error404 isTenant={isTenant} />}
      />
    </Routes>
  );
}

export function App() {
  const isServer = typeof window === 'undefined';

  const Router = isServer
    ? ({ children }: { children: React.ReactNode }) => (
        <StaticRouter location={getPathname()}>{children}</StaticRouter>
      )
    : ({ children }: { children: React.ReactNode }) => <BrowserRouter>{children}</BrowserRouter>;

  return (
    <Provider store={store}>
      <ToastProvider>
        <GoogleAnalytics />
        <Router>
          <NavigationProvider>
            <ErrorBoundary isTenant={getPathname().includes('/tenant')}>
              <HelmetProvider>
                <Content />
              </HelmetProvider>
            </ErrorBoundary>
          </NavigationProvider>
        </Router>
      </ToastProvider>
    </Provider>
  );
}
